import { Link } from "@tanstack/react-router";
import { ArrowRight, Lock } from "lucide-react";
import { ink, tint } from "@/data/feelings-pathway";
import { supportOptionsFor, type SupportOption } from "@/data/feelings-support-options";
import type { CoreFeeling } from "@/data/feelings-pathway";

export type SupportOptionsProps = {
  feeling: CoreFeeling | null;
  hasMemberAccess: boolean;
  /** Opens the optional reflection prompts. */
  onReflect: () => void;
  /** Opens the "what do you need?" helper. */
  onHelpMeChoose: () => void;
  /** Called when a practice link is followed, so the check-in can remember it. */
  onOpenPractice?: (title: string) => void;
};

function Shell({
  option,
  color,
  children,
}: {
  option: SupportOption;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <span className="flex items-start gap-2.5">
        <span aria-hidden className="text-xl leading-none">
          {option.emoji}
        </span>
        <span>
          <span className="block text-[15px] font-semibold leading-snug" style={{ color: ink(color) }}>
            {option.title}
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-foreground/75">{option.line}</span>
        </span>
      </span>
      {children}
    </>
  );
}

/**
 * The first screen after a feeling is chosen: immediate, low-effort support.
 *
 * Every option is a single tap. Nothing here asks the person to write, decide
 * between abstract needs, or read anything long before they get help.
 */
export function SupportOptions({
  feeling,
  hasMemberAccess,
  onReflect,
  onHelpMeChoose,
  onOpenPractice,
}: SupportOptionsProps) {
  const color = feeling?.color ?? "#C9A227";
  const options = supportOptionsFor(feeling);

  const cardStyle = {
    background: `linear-gradient(160deg, ${tint(color, 12)}, ${tint(color, 34)})`,
    borderColor: `color-mix(in oklab, ${color} 40%, white)`,
  };
  const cardClass =
    "on-tint flex h-full min-h-[104px] w-full flex-col justify-between gap-3 rounded-2xl border-2 p-4 text-start transition hover:-translate-y-0.5 hover:shadow-md";

  return (
    <div>
      <h3 className="font-display text-lg text-foreground">
        {feeling
          ? `You're feeling ${feeling.label.toLowerCase()}. What might help right now?`
          : "What might help right now?"}
      </h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Pick whatever feels possible. There's no right answer, and you can come back to the others
        any time.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {options.map((o) => {
          if (o.key === "reflect" || o.key === "unsure") {
            return (
              <button
                key={o.key}
                type="button"
                onClick={o.key === "reflect" ? onReflect : onHelpMeChoose}
                style={cardStyle}
                className={cardClass}
              >
                <Shell option={o} color={color}>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: ink(color) }}
                  >
                    {o.key === "reflect" ? "Open the prompts" : "Help me choose"}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </span>
                </Shell>
              </button>
            );
          }

          const locked = Boolean(o.memberOnly) && !hasMemberAccess;
          return (
            <Link
              key={o.key}
              to={locked ? "/membership" : (o.to as string).split("?")[0]}
              search={
                locked || !(o.to as string).includes("?")
                  ? undefined
                  : (Object.fromEntries(new URLSearchParams((o.to as string).split("?")[1])) as never)
              }
              onClick={() => onOpenPractice?.(o.title)}
              style={cardStyle}
              className={cardClass}
            >
              <Shell option={o} color={color}>
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold"
                  style={{ color: ink(color) }}
                >
                  {locked ? (
                    <>
                      <Lock className="h-3.5 w-3.5" aria-hidden />
                      Members only — see membership
                    </>
                  ) : (
                    <>
                      Open this <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </>
                  )}
                </span>
              </Shell>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
